import React from "react";
import { BookOpen, Palette, Sparkles, Pencil } from "lucide-react";
import { ASSETS, LINKS, BRAND } from "@/lib/brand";

export const BookPreview = () => {
  return (
    <section
      id="buch"
      data-testid="book-section"
      className="relative py-24 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl mb-14">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-paula-blue mb-4">
            Buchvorschau
          </div>
          <h2 className="font-display font-bold text-4xl sm:text-5xl text-paula-ink leading-[1.05] tracking-tight">
            Ein Ausmalbuch, das man fühlt —{" "}
            <span className="text-paula-muted">und nie vergisst.</span>
          </h2>
          <p className="mt-6 text-paula-muted text-lg leading-relaxed">
            Geschrieben und erfunden von Simone Faller. In kindgerechter
            Sprache. Mit Ausmalseiten, die zum Mitmachen einladen — und der
            HELD-Regel als Kompass.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          {/* Cover */}
          <div className="lg:col-span-5 lg:row-span-2">
            <div className="group relative h-full rounded-3xl bg-white border border-slate-200 overflow-hidden p-3 flex flex-col min-h-[480px]">
              <div className="relative rounded-2xl overflow-hidden flex-1">
                <img
                  src={ASSETS.bookCover}
                  alt="Buchcover Bleib cool — das kannst auch Du!"
                  className="w-full h-full object-cover"
                  data-testid="book-cover-image"
                />
              </div>
              <div className="p-5 pt-6 flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-paula-muted">
                    Ausmalbuch · {BRAND.pages} Seiten · ISBN {BRAND.isbn}
                  </div>
                  <div className="font-display font-bold text-paula-ink text-lg leading-tight mt-1">
                    {BRAND.bookTitle}
                  </div>
                  <div className="text-sm text-paula-muted mt-0.5">
                    von {BRAND.author}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-display font-bold text-paula-red text-2xl">
                    {BRAND.price}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Feature: kindgerechte Sprache */}
          <div className="lg:col-span-4">
            <div
              data-testid="book-feature-language"
              className="rounded-3xl bg-paula-red text-white p-7 h-64 lg:h-full flex flex-col justify-between"
            >
              <BookOpen size={28} />
              <div>
                <div className="text-white/80 text-[10px] uppercase tracking-widest">
                  Kindgerecht erzählt
                </div>
                <div className="font-display font-bold text-2xl leading-snug mt-1">
                  Paula erklärt den Notruf — und die HELD-Regel.
                </div>
                <p className="text-white/85 text-sm mt-3 leading-relaxed">
                  Schritt für Schritt: Was tun, wenn jemand stürzt, blutet oder
                  bewusstlos ist.
                </p>
              </div>
            </div>
          </div>

          {/* Feature: Ausmalen */}
          <div className="lg:col-span-3">
            <div className="rounded-3xl bg-paula-yellow p-6 h-64 lg:h-full flex flex-col justify-between">
              <Pencil className="text-paula-ink" size={28} />
              <div>
                <div className="text-paula-ink/70 text-[10px] uppercase tracking-widest">
                  Zum Selbermachen
                </div>
                <div className="font-display font-bold text-paula-ink text-2xl leading-snug mt-1">
                  Ausmalseiten für kleine Helden.
                </div>
              </div>
            </div>
          </div>

          {/* 3D Anmal-Paula */}
          <div className="lg:col-span-4">
            <div
              data-testid="book-3d-coloring"
              className="rounded-3xl bg-paula-blue p-7 h-64 lg:h-auto text-white flex flex-col justify-between relative overflow-hidden"
            >
              <div
                aria-hidden
                className="absolute inset-y-0 right-0 w-1/2 bg-cover bg-center opacity-90"
                style={{ backgroundImage: `url(${ASSETS.anmalPaula3d})` }}
              />
              <div
                aria-hidden
                className="absolute inset-0 bg-gradient-to-r from-paula-blue via-paula-blue/85 to-paula-blue/30"
              />
              <div className="relative">
                <Palette size={28} />
              </div>
              <div className="relative">
                <div className="text-white/80 text-[10px] uppercase tracking-widest">
                  Inklusive
                </div>
                <div className="font-display font-bold text-2xl leading-snug mt-1">
                  3D-Anmal-Paula.
                </div>
                <p className="text-white/85 text-sm mt-3 leading-relaxed">
                  3D-gedruckt, ca. 10 cm groß — zum eigenen Anmalen.
                </p>
              </div>
            </div>
          </div>

          {/* Innenseite teaser */}
          <div className="lg:col-span-3">
            <div className="relative rounded-3xl bg-paula-ink text-white p-7 h-64 lg:h-auto flex flex-col justify-between overflow-hidden">
              <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-paula-yellow/20 blur-2xl" />
              <div className="relative flex items-center gap-2">
                <span className="px-2 py-1 rounded-full bg-paula-yellow text-paula-ink text-[10px] font-bold uppercase tracking-widest">
                  Band 2
                </span>
                <span className="text-[10px] uppercase tracking-widest text-white/60">
                  In Vorbereitung
                </span>
              </div>
              <div className="relative">
                <div className="font-display font-bold text-2xl leading-snug">
                  „RTW ist kein Taxi sondern für echte Notfälle!"
                </div>
                <p className="text-white/75 text-sm mt-3 leading-relaxed">
                  Paulas zweites Abenteuer ist fertig geschrieben — Druck folgt.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* CTAs unter dem Grid */}
        <div className="mt-10 flex flex-wrap gap-3 items-center">
          <a
            href={LINKS.shop}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="book-shopify-cta"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-paula-ink text-white font-semibold hover:bg-paula-red transition-colors"
          >
            Im Shopify-Shop kaufen
          </a>
          <a
            href={LINKS.amazon}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="book-amazon-cta"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white border-2 border-paula-ink text-paula-ink font-semibold hover:bg-paula-yellow-soft transition-colors"
          >
            Bei Amazon.de
          </a>
        </div>
      </div>
    </section>
  );
};

export default BookPreview;
